// Team roster + management (PRD §2, §4.9). Reads the live members + pending
// invites (both client-readable per firestore.rules); all writes go through
// callables so member docs stay client-immutable (CLAUDE.md rule 2).
import { useEffect, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import { stripUndefined } from './callable';
import { useTenant } from './tenant';
import { useAuth } from './useAuth';

export type TeamRole = 'owner' | 'admin' | 'agent';
export type InvitableRole = 'admin' | 'agent';

export interface Member {
  uid: string;
  email: string;
  role: TeamRole;
  isSelf: boolean;
}

export interface PendingInvite {
  id: string;
  email: string;
  role: InvitableRole;
}

export interface TeamState {
  members: Member[];
  invites: PendingInvite[];
  loading: boolean;
}

export function useTeam(): TeamState {
  const { tenantId } = useTenant();
  const { user } = useAuth();
  const [members, setMembers] = useState<Member[]>([]);
  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) { setMembers([]); setInvites([]); setLoading(false); return; }
    const unsubMembers = onSnapshot(
      collection(db, `tenants/${tenantId}/members`),
      (snap) => {
        setMembers(
          snap.docs.map((d) => {
            const m = d.data() as { email?: string; role?: TeamRole };
            return {
              uid: d.id,
              email: m.email ?? '',
              role: m.role ?? 'agent',
              isSelf: d.id === user?.uid,
            };
          }),
        );
        setLoading(false);
      },
      () => setLoading(false),
    );
    const unsubInvites = onSnapshot(
      collection(db, `tenants/${tenantId}/invites`),
      (snap) => {
        setInvites(
          snap.docs
            .map((d) => ({ id: d.id, ...(d.data() as { email: string; role: InvitableRole; status?: string }) }))
            .filter((i) => (i.status ?? 'pending') === 'pending')
            .map((i) => ({ id: i.id, email: i.email, role: i.role })),
        );
      },
      () => setInvites([]),
    );
    return () => { unsubMembers(); unsubInvites(); };
  }, [tenantId, user?.uid]);

  return { members, invites, loading };
}

// ── mutation wrappers (callables) ────────────────────────────────────────────
function call<TReq, TRes = { ok: boolean }>(name: string) {
  const fn = httpsCallable<TReq, TRes>(functions, name);
  return (data: TReq) => fn(stripUndefined(data)).then((r) => r.data);
}

export const inviteMember = call<
  { email: string; role: InvitableRole },
  { inviteId: string; status: string }
>('tenantInviteMember');
export const updateMemberRole = call<{ uid: string; role: InvitableRole }>('tenantUpdateMemberRole');
export const removeMember = call<{ uid: string }>('tenantRemoveMember');
